import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import {
  AutocompleteTextFilterConfig,
  TableSettings,
  TextFilterConfig,
} from 'src/app/shared/components/smart/table/table.component';
import { Filter } from './base/database.service';

/**
 * Structure of persisted table state.
 * Only structural preferences are stored, no sensitive data.
 */
export interface PersistedTableState {
  pageIndex?: number;
  pageSize?: number;
  columns?: {
    id: string;
    visible: boolean;
    sort?: 'asc' | 'desc';
    filters: Filter[];
  }[];
  textFilterConfig?: TextFilterConfig;
  autocompleteTextFilterConfig?: AutocompleteTextFilterConfig;
}

/**
 * Service to persist table state across navigation using sessionStorage.
 * Provides centralized state management for table components.
 */
@Injectable({
  providedIn: 'root',
})
export class TableStatePersistenceService {
  private saveQueue$ = new Subject<{
    key: string;
    state: PersistedTableState;
  }>();

  private readonly STORAGE_PREFIX = 'table-state:';

  constructor() {
    this.saveQueue$.pipe(debounceTime(300)).subscribe(({ key, state }) => {
      this._performSave(key, state);
    });
  }

  /**
   * Perform the actual save operation to sessionStorage.
   * This is called after debouncing.
   * @param key Storage key
   * @param state State to save
   */
  private _performSave(key: string, state: PersistedTableState): void {
    try {
      const serialized = JSON.stringify(state);
      sessionStorage.setItem(key, serialized);
    } catch (error) {
      console.error(
        'TableStatePersistence Error saving state to sessionStorage:',
        error,
      );
    }
  }

  /**
   * Save TableSettings to sessionStorage by converting it to PersistedTableState.
   * This is the main method to call from the table component.
   * @param settings The TableSettings object from the table component
   * @param customPath Custom path - usually the router path
   * @param tableName Unique identifier for this table - usually the entity name
   */
  saveTableStateBySettings(
    settings: TableSettings,
    customPath: string,
    tableName: string,
  ): void {
    try {
      const persistedState: PersistedTableState = {
        pageIndex: settings.pageIndex ?? 0,
        pageSize: settings.pageSize ?? 20,
        columns: settings.columns.map((col) => ({
          id: col.id,
          visible: col.visible,
          sort: col.sort,
          filters: col.filters ?? [],
        })),
        textFilterConfig: settings.textFilterConfig,
        autocompleteTextFilterConfig: settings.autocompleteTextFilterConfig,
      };

      this._queueTableState(persistedState, customPath, tableName);
    } catch (error) {
      console.error('TableStatePersistence Error saving TableSettings:', error);
    }
  }

  /**
   * Save table state to sessionStorage with debouncing.
   * This method queues the save operation and debounces it by 300ms.
   * @param state The table state to persist
   * @param customPath Custom path
   * @param tableName Table identifier for multiple tables per route
   */
  private _queueTableState(
    state: PersistedTableState,
    customPath: string,
    tableName: string,
  ): void {
    try {
      const key = this._getStorageKey(customPath, tableName);
      this.saveQueue$.next({ key, state });
    } catch (error) {
      console.error('TableStatePersistence Error queueing state save:', error);
    }
  }

  /**
   * Generate a unique storage key based on the current route path and optional table name.
   * @param customPath Custom path
   * @param tableName Table identifier for multiple tables per route
   * @returns Storage key (e.g., 'table-state:/firm/clients:cp_users')
   */
  private _getStorageKey(customPath: string, tableName: string): string {
    const key = `${this.STORAGE_PREFIX}${customPath}`;
    return tableName ? `${key}:${tableName}` : key;
  }

  /**
   * Load table state from sessionStorage.
   * @param customPath Custom path - usually the router path
   * @param tableName Table identifier for multiple tables per route - usually the entity name
   * @returns Persisted state or null if not found/invalid
   */
  loadState(customPath: string, tableName: string): PersistedTableState | null {
    try {
      const key = this._getStorageKey(customPath, tableName);
      const serialized = sessionStorage.getItem(key);

      if (!serialized) {
        return null;
      }

      const state = JSON.parse(serialized) as PersistedTableState;

      if (!this._validateSessionStorageTableState(state)) {
        console.warn('TableStatePersistence Invalid state found for key:', key);
        return null;
      }

      return state;
    } catch (error) {
      console.error(
        'TableStatePersistence Error loading state from sessionStorage:',
        error,
      );
      return null;
    }
  }

  private _validateSessionStorageTableState(
    state: PersistedTableState,
  ): boolean {
    return (
      (state.pageIndex == null || typeof state.pageIndex === 'number') &&
      (state.pageSize == null || typeof state.pageSize === 'number') &&
      (state.columns == null || Array.isArray(state.columns)) &&
      (state.textFilterConfig == null ||
        typeof state.textFilterConfig === 'object') &&
      (state.autocompleteTextFilterConfig == null ||
        (typeof state.autocompleteTextFilterConfig === 'object' &&
          Array.isArray(state.autocompleteTextFilterConfig?.columns) &&
          state.autocompleteTextFilterConfig.columns.length > 0))
    );
  }
}
